import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || ''
const NAVY    = '#002E53'
const GOLD    = '#C5A059'
const FONT    = "'Poppins', sans-serif"

// Generate a stable session ID per page load
function makeSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

const SESSION_ID = makeSessionId()

// Quick-start suggestion pills shown when chat is empty
const QUICK_SUGGESTIONS = [
  { label: '🏙️ Browse off-plan projects', message: 'Show me the available off-plan projects' },
  { label: '📊 UAE market insights', message: 'What is the current UAE real estate market like?' },
  { label: '💰 Mortgage calculator', message: 'How does the mortgage calculator work?' },
  { label: '📈 Investment advice', message: 'What are the best areas to invest in Dubai right now?' },
  { label: '🤝 Speak to an agent', message: 'How can I get in touch with a Victory Homes agent?' },
]

// ── Typing indicator ────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '5px',
      padding: '12px 16px',
      background: 'rgba(255,255,255,0.07)',
      borderRadius: '16px 16px 16px 4px',
      width: 'fit-content', maxWidth: '80px',
    }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: '7px', height: '7px', borderRadius: '50%',
          background: GOLD, opacity: 0.7,
          animation: `chatDot 1.4s ease-in-out ${i * 0.2}s infinite`,
          display: 'inline-block',
        }} />
      ))}
    </div>
  )
}

// ── Single message bubble ───────────────────────────────────────────────────
function MessageBubble({ msg, onLinkClick }) {
  const isUser = msg.role === 'user'
  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: '12px',
      animation: 'chatFadeIn 0.3s ease-out',
    }}>
      {/* Bot avatar */}
      {!isUser && (
        <div style={{
          width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${NAVY}, #001a33)`,
          border: `1.5px solid ${GOLD}60`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', marginRight: '8px', marginTop: '2px',
        }}>
          🤖
        </div>
      )}

      <div style={{ maxWidth: '82%' }}>
        {/* Text bubble */}
        <div style={{
          padding: '11px 15px',
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: isUser
            ? `linear-gradient(135deg, ${GOLD}, #b8904a)`
            : 'rgba(255,255,255,0.08)',
          color: isUser ? '#fff' : 'rgba(255,255,255,0.92)',
          fontFamily: FONT, fontSize: '13.5px', lineHeight: 1.65,
          border: isUser ? 'none' : '1px solid rgba(255,255,255,0.1)',
          backdropFilter: isUser ? 'none' : 'blur(8px)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {msg.text}
        </div>

        {/* Navigation link chips */}
        {msg.links?.length > 0 && (
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {msg.links.map((lnk, i) => (
              <a
                key={i}
                href={lnk.url}
                onClick={() => onLinkClick && onLinkClick(lnk)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px',
                  background: `linear-gradient(135deg, ${NAVY}cc, #001a33cc)`,
                  border: `1px solid ${GOLD}50`,
                  borderRadius: '10px', color: GOLD,
                  fontFamily: FONT, fontSize: '12.5px', fontWeight: 600,
                  textDecoration: 'none', cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(8px)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${GOLD}22, ${GOLD}15)`
                  e.currentTarget.style.borderColor = GOLD
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${NAVY}cc, #001a33cc)`
                  e.currentTarget.style.borderColor = `${GOLD}50`
                }}
              >
                <span style={{ fontSize: '14px' }}>→</span>
                {lnk.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Chatbot component ──────────────────────────────────────────────────
function AIChatbot() {
  const location   = useLocation()
  const [open, setOpen]         = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [pulse, setPulse]       = useState(true)
  const [hovered, setHovered]   = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef       = useRef(null)

  // Stop pulse after first open
  useEffect(() => {
    if (open) setPulse(false)
  }, [open])

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Focus input when chat opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150)
  }, [open])

  const sendMessage = useCallback(async (text) => {
    const trimmed = text?.trim()
    if (!trimmed || loading) return

    // Append user message
    const userMsg = { role: 'user', text: trimmed }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)

    // Build history (exclude the latest user message — we send it separately)
    const history = messages.map(m => ({ role: m.role, text: m.text }))

    try {
      const res = await fetch(`${API_URL}/api/chatbot/chat`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message:   trimmed,
          history,
          sessionId: SESSION_ID,
          pageUrl:   location.pathname,
        }),
      })

      if (!res.ok) throw new Error(`Server ${res.status}`)
      const data = await res.json()

      setMessages(prev => [...prev, {
        role:  'assistant',
        text:  data.reply || 'I apologise, something went wrong. Please try again.',
        links: data.links || [],
      }])
    } catch (err) {
      console.error('[AIChatbot] Error:', err)
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: '⚠️ I\'m having trouble connecting right now. Please try again in a moment, or contact us directly via WhatsApp.',
        links: [{ label: 'Contact Us', url: '/contact' }],
      }])
    } finally {
      setLoading(false)
    }
  }, [loading, messages, location.pathname])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const handleOpen = () => {
    setOpen(true)
    // Send greeting if no messages yet
    if (messages.length === 0) {
      setTimeout(() => {
        setMessages([{
          role: 'assistant',
          text: 'Hello! 👋 I\'m the Victory Homes AI Assistant.\n\nI can help you explore our UAE properties, understand the market, calculate your ROI, or navigate our website. How can I assist you today?',
          links: [],
        }])
      }, 300)
    }
  }

  return (
    <>
      <style>{`
        @keyframes chatDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30%            { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes chatFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes chatBtnPulse {
          0%   { box-shadow: 0 0 0 0 rgba(197,160,89,0.6), 0 4px 20px rgba(0,46,83,0.4); }
          70%  { box-shadow: 0 0 0 14px rgba(197,160,89,0), 0 4px 20px rgba(0,46,83,0.4); }
          100% { box-shadow: 0 0 0 0 rgba(197,160,89,0), 0 4px 20px rgba(0,46,83,0.4); }
        }
        .chat-panel-scroll::-webkit-scrollbar { width: 4px; }
        .chat-panel-scroll::-webkit-scrollbar-track { background: transparent; }
        .chat-panel-scroll::-webkit-scrollbar-thumb { background: rgba(197,160,89,0.3); border-radius: 4px; }
        .chat-input-field::placeholder { color: rgba(255,255,255,0.35); }
        .chat-input-field:focus { outline: none; }
        .chat-send-btn:hover { transform: scale(1.08); }
        .chat-send-btn:active { transform: scale(0.96); }
        .chat-toggle-btn:hover { transform: scale(1.1) translateY(-2px) !important; }
        .chat-toggle-btn:active { transform: scale(0.96) translateY(0) !important; }
        .chat-suggestion-pill:hover {
          background: rgba(197,160,89,0.15) !important;
          border-color: ${GOLD} !important;
          color: ${GOLD} !important;
        }
        @media (max-width: 480px) {
          .chat-panel { 
            width: calc(100vw - 24px) !important; 
            right: 12px !important; 
            top: 16px !important;
            bottom: 165px !important;
            height: auto !important;
            max-height: none !important;
          }
        }
      `}</style>

      {/* ── Toggle button (above WhatsApp) ─────────────────────────────── */}
      <button
        id="ai-chatbot-toggle"
        className="chat-toggle-btn"
        aria-label={open ? 'Close AI Assistant' : 'Open AI Assistant'}
        onClick={open ? () => setOpen(false) : handleOpen}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position:     'fixed',
          bottom:       '96px',
          right:        '24px',
          zIndex:       9998,
          width:        '58px',
          height:       '58px',
          borderRadius: '50%',
          border:       'none',
          cursor:       'pointer',
          background:   open || hovered
            ? `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`
            : `linear-gradient(135deg, #002e53 0%, #001a33 100%)`,
          boxShadow:    pulse
            ? undefined
            : `0 4px 20px rgba(0,46,83,0.4), inset 0 1px 0 rgba(255,255,255,0.1)`,
          animation:    pulse ? 'chatBtnPulse 2.4s ease-out infinite' : 'none',
          transition:   'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'center',
          overflow:     'hidden',
        }}
      >
        {/* Shimmer ring border */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: `1.5px solid ${open ? GOLD + '80' : GOLD + '50'}`,
          transition: 'border-color 0.3s',
        }} />

        {/* Icon */}
        <div style={{
          position: 'relative', zIndex: 1,
          transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          transform:  open ? 'rotate(90deg) scale(0.9)' : 'rotate(0deg) scale(1)',
          fontSize: '24px',
          lineHeight: 1,
        }}>
          {open ? '✕' : '🤖'}
        </div>

        {/* Tooltip */}
        {!open && hovered && (
          <div style={{
            position:   'absolute',
            right:      '68px',
            top:        '50%',
            transform:  'translateY(-50%)',
            background: `linear-gradient(135deg, ${NAVY}, #001a33)`,
            border:     `1px solid ${GOLD}45`,
            color:      '#fff',
            fontFamily: FONT, fontSize: '12px', fontWeight: 500,
            padding:    '7px 13px', borderRadius: '8px',
            whiteSpace: 'nowrap', pointerEvents: 'none',
            boxShadow:  '0 4px 16px rgba(0,0,0,0.3)',
            animation:  'chatFadeIn 0.2s ease-out',
          }}>
            AI Assistant
            {/* Arrow */}
            <div style={{
              position: 'absolute', right: '-6px', top: '50%',
              transform: 'translateY(-50%) rotate(45deg)',
              width: '10px', height: '10px',
              background: NAVY,
              borderTop: `1px solid ${GOLD}45`,
              borderRight: `1px solid ${GOLD}45`,
            }} />
          </div>
        )}
      </button>

      {/* ── Chat panel ─────────────────────────────────────────────────────── */}
      {open && (
        <div
          className="chat-panel"
          style={{
            position:     'fixed',
            bottom:       '168px',
            right:        '24px',
            zIndex:       9997,
            width:        '370px',
            height:       '520px',
            maxHeight:    'calc(100dvh - 190px)',
            borderRadius: '20px',
            display:      'flex',
            flexDirection: 'column',
            overflow:     'hidden',
            background:   `linear-gradient(160deg, rgba(0,25,50,0.97) 0%, rgba(0,10,25,0.99) 100%)`,
            border:       `1px solid rgba(197,160,89,0.25)`,
            boxShadow:    '0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(197,160,89,0.08)',
            backdropFilter: 'blur(20px)',
            animation:    'chatSlideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          {/* ── Header ─────────────────────────────────────────────── */}
          <div style={{
            padding:    '16px 18px',
            background: `linear-gradient(135deg, ${NAVY}ee 0%, #001830ee 100%)`,
            borderBottom: `1px solid rgba(197,160,89,0.2)`,
            display:    'flex', alignItems: 'center', gap: '12px', flexShrink: 0,
          }}>
            {/* Logo mark */}
            <div style={{
              width: '38px', height: '38px', borderRadius: '10px',
              background: `linear-gradient(135deg, ${GOLD}22, ${GOLD}10)`,
              border: `1.5px solid ${GOLD}50`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, padding: '4px'
            }}>
              <img src="/assets/Victory Homes logo design.png" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>

            {/* Title + status */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                color: '#fff', letterSpacing: '0.2px',
              }}>
                Victory Homes AI
              </div>
              <div style={{
                fontFamily: FONT, fontSize: '11px', color: `${GOLD}cc`,
                display: 'flex', alignItems: 'center', gap: '5px', marginTop: '1px',
              }}>
                <span style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  background: '#4ade80', display: 'inline-block',
                  boxShadow: '0 0 6px #4ade80', flexShrink: 0,
                }} />
                UAE Real Estate Assistant
              </div>
            </div>

            {/* Close */}
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              style={{
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.6)', borderRadius: '8px',
                width: '30px', height: '30px', cursor: 'pointer', fontSize: '14px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)' }}
            >
              ✕
            </button>
          </div>

          {/* ── Messages area ──────────────────────────────────────── */}
          <div
            className="chat-panel-scroll"
            style={{
              flex: 1, overflowY: 'auto',
              padding: '16px 14px',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                  background: `linear-gradient(135deg, ${NAVY}, #001a33)`,
                  border: `1.5px solid ${GOLD}60`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', marginRight: '8px', marginTop: '2px',
                }}>
                  🤖
                </div>
                <TypingIndicator />
              </div>
            )}

            {/* Quick suggestions (only when 1 message shown — the greeting) */}
            {messages.length === 1 && !loading && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{
                  fontFamily: FONT, fontSize: '11px', fontWeight: 600,
                  color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px',
                  textTransform: 'uppercase', marginBottom: '4px',
                }}>
                  Popular questions
                </div>
                {QUICK_SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    className="chat-suggestion-pill"
                    onClick={() => sendMessage(s.message)}
                    style={{
                      background:   'rgba(255,255,255,0.05)',
                      border:       '1px solid rgba(255,255,255,0.12)',
                      borderRadius: '10px',
                      color:        'rgba(255,255,255,0.75)',
                      fontFamily:   FONT, fontSize: '12.5px', fontWeight: 500,
                      padding:      '9px 14px', cursor: 'pointer',
                      textAlign:    'left', transition: 'all 0.2s ease',
                      width:        '100%',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* ── Input area ─────────────────────────────────────────── */}
          <div style={{
            padding:      '12px 14px',
            borderTop:    '1px solid rgba(255,255,255,0.08)',
            background:   'rgba(0,0,0,0.2)',
            flexShrink:   0,
          }}>
            <div style={{
              display:      'flex', alignItems: 'center', gap: '10px',
              background:   'rgba(255,255,255,0.07)',
              border:       '1px solid rgba(255,255,255,0.12)',
              borderRadius: '14px',
              padding:      '10px 14px',
              transition:   'border-color 0.2s',
            }}
              onFocusCapture={e => e.currentTarget.style.borderColor = `${GOLD}50`}
              onBlurCapture={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'}
            >
              <textarea
                ref={inputRef}
                className="chat-input-field"
                rows={1}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about UAE properties..."
                disabled={loading}
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  color: '#fff', fontFamily: FONT, fontSize: '13.5px',
                  resize: 'none', lineHeight: 1.5, maxHeight: '80px',
                  overflowY: 'auto',
                }}
              />

              {/* Send button */}
              <button
                className="chat-send-btn"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                aria-label="Send message"
                style={{
                  width:        '34px',
                  height:       '34px',
                  borderRadius: '10px',
                  border:       'none',
                  background:   input.trim() && !loading
                    ? `linear-gradient(135deg, ${GOLD}, #b8904a)`
                    : 'rgba(255,255,255,0.1)',
                  color:        input.trim() && !loading ? '#fff' : 'rgba(255,255,255,0.3)',
                  cursor:       input.trim() && !loading ? 'pointer' : 'not-allowed',
                  display:      'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize:     '16px', flexShrink: 0,
                  transition:   'all 0.2s ease',
                  boxShadow:    input.trim() && !loading ? `0 4px 12px ${GOLD}40` : 'none',
                }}
              >
                {loading
                  ? <span style={{ fontSize: '12px', animation: 'chatDot 1.4s infinite' }}>⏳</span>
                  : '➤'
                }
              </button>
            </div>

            {/* Disclaimer */}
            <div style={{
              fontFamily: FONT, fontSize: '10px', color: 'rgba(255,255,255,0.25)',
              textAlign: 'center', marginTop: '8px', letterSpacing: '0.2px',
            }}>
              Victory Homes AI · UAE Real Estate Only · Powered by Gemini
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AIChatbot
